import { useMemo } from 'react';
import { useDimensions, useCriticalEvents } from '@src/services/queries';

export interface BaseQueryPayload {
  pattern?: string[];
  group_by?: {
    field: string;
  }[];
  filters?: FilterValue;
}

export const useQueryPayloadWithDeps = <T extends BaseQueryPayload>(payload: T) => {
  const currentDimensions = useMemo(
    () =>
      new Set([
        ...(payload.group_by?.map((it) => it.field) ?? []),
        ...(payload.filters?.flat().map((it) => it.field) ?? []),
      ]),
    [payload.group_by, payload.filters],
  );
  const currentEvents = useMemo(() => new Set(payload.pattern), [payload.pattern]);
  const {
    isLoading: isLoadingDimensions,
    isFetching: isFetchingDimensions,
    data: dimensionsData,
  } = useDimensions({
    enabled: currentDimensions.size > 0,
  });
  const {
    isLoading: isLoadingCriticalEvents,
    isFetching: isFetchingCriticalEvents,
    data: criticalEventsData,
  } = useCriticalEvents({
    enabled: currentEvents.size > 0,
  });

  const dimensionQueryKeys = Array.from(currentDimensions).map((it) => [
    it,
    dimensionsData?.dimensionMap.get(it)?.updated_at,
  ]);
  const eventQueryKeys = Array.from(currentEvents).map((it) => [
    it,
    criticalEventsData?.criticalEventMap.get(it)?.updated_at,
  ]);
  const hasInvalidDimensions = dimensionQueryKeys.some(([_key, updatedAt]) => !updatedAt);
  const hasInvalidEvents = eventQueryKeys.some(([_key, updatedAt]) => !updatedAt);

  return {
    depKeys: {
      dimensionQueryKeys,
      eventQueryKeys,
    },
    getEnabledStatus: (enabled: boolean) =>
      enabled &&
      !isLoadingDimensions &&
      !isLoadingCriticalEvents &&
      !hasInvalidDimensions &&
      !hasInvalidEvents,
    getLoadingStatus: (isLoading: boolean) =>
      isLoading || isLoadingDimensions || isLoadingCriticalEvents,
    getFetchingStatus: (isFetching: boolean) =>
      isFetching ||
      (hasInvalidDimensions && isFetchingDimensions) ||
      (hasInvalidEvents && isFetchingCriticalEvents),
  };
};
